import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";

import type {Locale} from "./language-picker-query.tsx";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";

type Props = {
    disabled: boolean;
    locales: Array<Locale>;
    defaultSelectedValue: string;
};

type State = {
    curLanguage: string;
    opened: boolean;
};

/**
 * This component displays the language picker UI and handles the logic to
 * switch between different languages
 */
class LanguagePickerSelect extends React.Component<Props, State> {
    static defaultProps: {
        disabled: boolean;
    } = {
        disabled: false,
    };

    state: State = {
        curLanguage: this.props.defaultSelectedValue,
        opened: false,
    };

    /**
     * Gets the current lang name from the current locale
     */
    getCurrentLanguageName(): string {
        const {curLanguage} = this.state;

        const selectedLocale = this.props.locales.find(
            (locale) => locale.kaLocale === curLanguage,
        );

        return selectedLocale ? selectedLocale.localName : curLanguage;
    }

    /**
     * Gets the appropriate subdomain for a given KA Locale.
     */
    getSubdomainFromKALocale(kaLocale: string): string {
        if (kaLocale === "en") {
            return "www";
        }
        if (kaLocale === "zh-hans") {
            return "zh";
        }
        return kaLocale;
    }

    /**
     * Returns the full locale-specific url
     */
    getLocaleURL(locale: Locale): string {
        return (
            "https://" +
            this.getSubdomainFromKALocale(locale.kaLocale) +
            ".khanacademy.org"
        );
    }

    renderActionItems(locales: Array<Locale>): any {
        return locales.map((locale: Locale) => (
            <ActionItem
                key={locale.kaLocale}
                label={locale.localName}
                lang={locale.kaLocale}
                href={this.getLocaleURL(locale)}
                testId={"language_picker_" + locale.kaLocale}
            />
        ));
    }

    render(): React.ReactNode {
        const {disabled, locales} = this.props;

        return (
            <div className={css(styles.languagePicker)}>
                <LabelXSmall>{t`Language`}</LabelXSmall>
                <ActionMenu
                    onToggle={(opened: boolean) => this.setState({opened})}
                    menuText={this.getCurrentLanguageName()}
                    disabled={disabled}
                    opener={({text}) => (
                        <button
                            aria-label={t`Change language`}
                            aria-expanded={this.state.opened}
                            className={css(styles.opener)}
                            data-testid="language_picker"
                        >
                            {text}
                            <PhosphorIcon
                                icon={caretDownIcon}
                                color={color.white}
                                style={{
                                    verticalAlign: "middle",
                                    width: 12,
                                    height: 12,
                                    marginLeft: "2px",
                                }}
                            />
                        </button>
                    )}
                >
                    {this.renderActionItems(locales)}
                </ActionMenu>
                {/* Render the links to give a boost to i18n SEO. */}
                <noscript>
                    {locales.map((locale: Locale) => (
                        <Link
                            key={locale.kaLocale}
                            href={this.getLocaleURL(locale)}
                        >
                            {locale.localName}
                        </Link>
                    ))}
                </noscript>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    // component container
    languagePicker: {
        display: "flex",
        alignItems: "center",
        [mediaQueries.smOrSmaller]: {
            justifyContent: "center",
        },
    },
    // The button which when clicked opens the menu
    opener: {
        background: "transparent",
        border: "none",
        height: "100%",
        color: color.white,
        paddingLeft: "8px",
        fontSize: "12px",
        fontFamily: "Lato",
        lineHeight: "16px",
        ":focus": {
            outline: "none",
            textDecoration: "underline",
        },
        ":hover": {
            outline: "none",
            textDecoration: "underline",
            cursor: "pointer",
        },
    },
});

export default LanguagePickerSelect;
